import { Pill, VisuallyHidden } from '@mantine/core';
import Image from 'next/image';
import type { ReactNode } from 'react';
import {
	HelpContent,
	HelpContentHeading,
} from '../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/HelpContent';
import styles from './EquipmentResponsibilityDetails.module.scss';
import equipmentResponsibility from './equipmentResponsibility.png';

enum EquipmentOwner {
	DTE = 'DTE',
	Customer = 'Customer',
	OtherUtility = 'OtherUtility',
}

const ownershipData = [
	{ name: 'Meter', owner: EquipmentOwner.DTE },
	{ name: 'Pole ground', owner: EquipmentOwner.DTE },
	{ name: 'Power pole', owner: EquipmentOwner.DTE },
	{ name: 'Power distribution lines', owner: EquipmentOwner.DTE },
	{ name: 'Power neutral line', owner: EquipmentOwner.DTE },
	{ name: 'Service line', owner: EquipmentOwner.DTE },
	{ name: 'Transformer', owner: EquipmentOwner.DTE },
	{ name: 'Communication lines', owner: EquipmentOwner.OtherUtility },
	{ name: 'Meter socket & jaws', owner: EquipmentOwner.Customer },
	{ name: 'Point of attachment', owner: EquipmentOwner.Customer },
	{ name: 'Service entrance conductor', owner: EquipmentOwner.Customer },
	{ name: 'Service mast', owner: EquipmentOwner.Customer },
	{ name: 'Service mast guy', owner: EquipmentOwner.Customer },
	{ name: 'Service panel', owner: EquipmentOwner.Customer },
	{ name: 'Tree trimming (around service line)', owner: EquipmentOwner.Customer },
	{ name: 'Weatherhead', owner: EquipmentOwner.Customer },
];

function EquipmentOwnershipTable(): ReactNode {
	return (
		<table className={styles['responsibilityTable']}>
			<thead>
				<tr>
					<th scope="col">Utility (DTE)</th>
					<th scope="col">Other Utilities</th>
					<th scope="col">Customer</th>
				</tr>
			</thead>
			<tbody className={styles['dteOwned']}>
				{ownershipData
					.filter((row) => row.owner === EquipmentOwner.DTE)
					.map((row) => (
						<tr key={row.name}>
							<td className={styles['owned']}>{row.name}</td>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
					))}
			</tbody>
			<tbody className={styles['otherUtilityOwned']}>
				{ownershipData
					.filter((row) => row.owner === EquipmentOwner.OtherUtility)
					.map((row) => (
						<tr key={row.name}>
							<td>&nbsp;</td>
							<td className={styles['owned']}>{row.name}</td>
							<td>&nbsp;</td>
						</tr>
					))}
			</tbody>
			<tbody className={styles['customerOwned']}>
				{ownershipData
					.filter((row) => row.owner === EquipmentOwner.Customer)
					.map((row) => (
						<tr key={row.name}>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
							<td className={styles['owned']}>{row.name}</td>
						</tr>
					))}
			</tbody>
		</table>
	);
}

export function EquipmentResponsibilityDetails(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Equipment responsibility</h4>
				<Pill.Group>
					<Pill>Utility owned and customer owned equipment</Pill>
				</Pill.Group>
			</HelpContentHeading>

			<figure style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: 1000 }}>
				<Image
					src={equipmentResponsibility}
					alt="Diagram of equipment with labels of what is DTE's responsibility and what is the customer's responsibility"
					style={{ width: '100%', height: 'auto', display: 'block' }}
				/>
				<VisuallyHidden component="figcaption">
					Diagram of equipment with labels indicating which equipment is DTE's responsibility and which
					equipment is the customer's responsibility.
				</VisuallyHidden>
			</figure>

			<EquipmentOwnershipTable />
		</HelpContent>
	);
}
